/**
 * C8 admin-followup (المتابعة الإدارية) view-model — pure display shaping for the
 * HR Action Center: it normalizes the mixed `action` vocabulary the HR-action log
 * carries (seed rows use activate/suspend/leave/edit while PATCH /status appends the
 * status value active/suspended/left), maps actions + employee statuses to .tag
 * modifier classes, and derives the month-summary KPI (leavers / suspensions /
 * edits) off the log. Kept out of the .vue so node:test can cover the logic
 * directly (the companies repo has no SFC test runner); the template just binds the
 * output. Plain CommonJS (require-able).
 */

// The log mixes two vocabularies for the same intents — seed rows use the verb
// (activate/suspend/leave/edit) while auto-appended rows from PATCH /employees/:id/
// status carry the resulting status (active/suspended/left). Fold both onto one
// canonical verb so labels, tags and the KPI stay consistent.
var ACTION_ALIAS = {
  activate: 'activate',
  active: 'activate',
  suspend: 'suspend',
  suspended: 'suspend',
  leave: 'leave',
  left: 'leave',
  edit: 'edit',
};

// canonical action → .vivo .tag modifier (mirrors the C8 mockup tags).
var ACTION_TAG = {
  activate: 'ok',
  suspend: 'review',
  leave: 'muted',
  edit: 'new',
};

// employee §2 status → .vivo .tag modifier.
var STATUS_TAG = {
  active: 'ok',
  suspended: 'review',
  left: 'muted',
};

/** Fold a raw log/status action onto its canonical verb. Unknown → 'edit'. */
function normalizeAction(action) {
  return ACTION_ALIAS[action] || 'edit';
}

/** Canonical action → .tag modifier class. Empty-safe. */
function actionTag(action) {
  return ACTION_TAG[normalizeAction(action)] || 'muted';
}

/** Employee status → .tag modifier class. Empty-safe. */
function statusTag(status) {
  return STATUS_TAG[status] || 'muted';
}

/** 'YYYY-MM' slice of an ISO date string. Empty-safe. */
function monthKeyOf(iso) {
  return String(iso || '').slice(0, 7);
}

/** Current 'YYYY-MM' key (UTC). `now` overridable for deterministic tests. */
function currentMonthKey(now) {
  var d = now ? new Date(now) : new Date();
  var y = d.getUTCFullYear();
  var m = d.getUTCMonth() + 1;
  return y + '-' + (m < 10 ? '0' + m : String(m));
}

/**
 * Month-summary KPI derived from the HR-action log: how many leavers, suspensions
 * and edits happened in `monthKey` (default = current month). Empty-safe.
 */
function monthSummary(log, monthKey) {
  var rows = log || [];
  var key = monthKey || currentMonthKey();
  var out = { leavers: 0, suspensions: 0, edits: 0 };
  for (var i = 0; i < rows.length; i++) {
    if (monthKeyOf(rows[i].date) !== key) continue;
    var a = normalizeAction(rows[i].action);
    if (a === 'leave') out.leavers += 1;
    else if (a === 'suspend') out.suspensions += 1;
    else if (a === 'edit') out.edits += 1;
  }
  return out;
}

/** Up-to-two-letter avatar initials from a full name. Empty-safe. */
function initials(name) {
  var parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '؟';
  if (parts.length === 1) return parts[0].slice(0, 2);
  return parts[0].slice(0, 1) + parts[1].slice(0, 1);
}

// ── D1 «تغيير الحالة السريع عاوز يظهر 10 موظفين فقط واي موظف تاني لما تعمل
//        بحث» ────────────────────────────────────────────────────────────────
//
// The quick-toggle panel used to render EVERY employee from one `limit: 100`
// fetch, and the page search box filtered that already-loaded array in the
// browser. Two problems: a company of 40 got a 40-row wall of buttons where it
// wanted a shortlist, and — worse — employee 101 onwards could not be found at
// all, because the search only ever saw the first page. The list is 10 rows and
// the search is a SERVER query now, so "any other employee" really means any.

var QUICK_TOGGLE_LIMIT = 10;

/** Query for the quick-toggle list. `limit`/`q` are the names the API accepts
 *  (`pageSize`/`search` are 400s), and a blank search is omitted entirely. */
function quickListParams(q) {
  var params = { limit: QUICK_TOGGLE_LIMIT };
  var needle = typeof q === 'string' ? q.trim() : '';
  if (needle) params.q = needle;
  return params;
}

/**
 * Which hint to show under the panel, so the shortlist never looks like the
 * whole company:
 *  - 'searchToFind' — default view, there are more employees than the 10 shown
 *  - 'searchEmpty'  — a search that matched nobody
 *  - ''             — nothing worth saying (all employees fit, or a search hit)
 */
function quickListHint(args) {
  var a = args || {};
  var shown = a.shown || 0;
  var searching = !!(typeof a.q === 'string' ? a.q.trim() : a.q);
  if (searching) return shown === 0 ? 'searchEmpty' : '';
  // No employees at all is not "the shortlist is partial" — it is an empty
  // company, and without this the panel rendered completely blank.
  if (shown === 0) return 'noEmployees';
  var total = a.total == null ? shown : a.total;
  return total > shown ? 'searchToFind' : '';
}

module.exports = {
  ACTION_ALIAS: ACTION_ALIAS,
  ACTION_TAG: ACTION_TAG,
  STATUS_TAG: STATUS_TAG,
  normalizeAction: normalizeAction,
  actionTag: actionTag,
  statusTag: statusTag,
  monthKeyOf: monthKeyOf,
  currentMonthKey: currentMonthKey,
  monthSummary: monthSummary,
  initials: initials,
  QUICK_TOGGLE_LIMIT: QUICK_TOGGLE_LIMIT,
  quickListParams: quickListParams,
  quickListHint: quickListHint,
};
